import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { FaSpinner, FaTimes, FaPlus } from "../utils/icons";
import { authService } from "../services/auth";
import { getApiBaseUrl } from "../config/api";
import { useAuth } from "../context/AuthContext";
import { useCohorts } from "../hooks/useStudyQueries";
import { Button } from "@/design-system/components/react";

function formatLatestActivity(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function SummaryCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string | number;
  detail: string;
}) {
  return (
    <div className="rounded-frame border border-border bg-surface-2 px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-fg">{value}</div>
      <div className="mt-0.5 text-xs text-fg-muted">{detail}</div>
    </div>
  );
}

function CohortMetric({ value }: { value: number | undefined }) {
  return <span className="font-mono tabular-nums">{value ?? "—"}</span>;
}

/** Admin: company overview plus cohort and member access management. */
export default function CompanyDetailPage() {
  const { companyId = "" } = useParams();
  const { token } = useAuth();
  const qc = useQueryClient();

  const companyQ = useQuery({
    queryKey: ["company", companyId],
    queryFn: () => authService.getCompany(companyId),
  });
  const readerCohortsQ = useCohorts(token);
  const labelingCohortsQ = useQuery({
    queryKey: ["admin-cohorts-list"],
    queryFn: async () => {
      const { token: t } = authService.getStoredAuth();
      const res = await axios.get(`${getApiBaseUrl()}/api/labeling/cohorts`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      return (res.data.cohorts ?? []) as string[];
    },
  });
  const usersQ = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => authService.getAdminUsers(),
    // /api/admin/users is a heavyweight scan and used only by the member
    // picker. Starting it beside the company request makes both calls take
    // several seconds on staging, so let the overview win the first render.
    enabled: companyQ.isSuccess,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["company", companyId] });
    qc.invalidateQueries({ queryKey: ["admin-companies"] });
  };
  const invalidateMembers = () => {
    invalidate();
    qc.invalidateQueries({ queryKey: ["admin-users"] });
  };

  const assignMut = useMutation({
    mutationFn: (cohortId: string) => authService.assignCompanyCohort(companyId, cohortId),
    onSuccess: invalidate,
  });
  const unassignMut = useMutation({
    mutationFn: (cohortId: string) => authService.unassignCompanyCohort(companyId, cohortId),
    onSuccess: invalidate,
  });
  const addMemberMut = useMutation({
    mutationFn: (userId: string) => authService.addCompanyMember(companyId, userId),
    onSuccess: invalidateMembers,
  });
  const removeMemberMut = useMutation({
    mutationFn: (userId: string) => authService.removeCompanyMember(companyId, userId),
    onSuccess: invalidateMembers,
  });

  const [cohortToAdd, setCohortToAdd] = useState("");
  const [userToAdd, setUserToAdd] = useState("");

  const detail = companyQ.data;
  const code = detail?.customer_code ?? "";
  const assignedIds = useMemo(
    () => new Set((detail?.cohorts ?? []).map((c) => c.cohort_id)),
    [detail],
  );
  const assignedElsewhere = useMemo(() => new Set(detail?.assigned_elsewhere ?? []), [detail]);
  const cohortSummaries = useMemo(
    () => new Map((readerCohortsQ.data ?? []).map((cohort) => [cohort.run_id, cohort])),
    [readerCohortsQ.data],
  );
  const assignedSummaries = useMemo(
    () =>
      (detail?.cohorts ?? []).map((cohort) => ({
        ...cohort,
        summary: cohortSummaries.get(cohort.cohort_id),
      })),
    [cohortSummaries, detail?.cohorts],
  );
  const knownSummaryCount = assignedSummaries.filter(
    (cohort) => cohort.summary !== undefined,
  ).length;
  const summarizedStudies = assignedSummaries.reduce(
    (total, cohort) => total + (cohort.summary?.total ?? 0),
    0,
  );
  const missingSummaryCount = assignedSummaries.length - knownSummaryCount;
  const studyCount =
    assignedSummaries.length === 0
      ? "0"
      : knownSummaryCount === 0
        ? "—"
        : `${summarizedStudies}${missingSummaryCount > 0 ? "+" : ""}`;
  // Exclusive-claim tenancy: offer every cohort not already assigned to THIS
  // company and not owned by another one. The customer code no longer gates
  // assignment (a cohort belongs to at most one company; the backend 409s a
  // cross-company claim) — so companies with no code, and legacy/unowned
  // cohorts, are assignable too.
  const available = (id: string) => !assignedIds.has(id) && !assignedElsewhere.has(id);
  const readerAvailable = (readerCohortsQ.data ?? []).map((c) => c.run_id).filter(available);
  const labelingAvailable = (labelingCohortsQ.data ?? []).filter(available);
  const memberIds = useMemo(() => new Set((detail?.members ?? []).map((m) => m.user_id)), [detail]);
  const nonMembers = (usersQ.data?.users ?? []).filter((u) => !memberIds.has(u.user_id));

  if (companyQ.isLoading) {
    return (
      <div className="p-8 text-fg-muted flex items-center gap-2">
        <FaSpinner className="animate-spin" /> Loading company…
      </div>
    );
  }
  if (companyQ.isError || !detail) {
    return <div className="p-8 text-warn">Failed to load company.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-6 text-fg">
      <Link to="/admin/companies" className="text-xs text-fg-muted hover:text-white">
        ← Companies
      </Link>
      <h1 className="text-2xl font-bold mt-2 mb-1">{detail.display_name}</h1>
      <div className="flex items-center gap-2 mb-6 text-xs font-mono text-fg-muted">
        <span>{detail.id}</span>
        {code ? (
          <span
            className="px-1.5 py-0.5 bg-signal-tint text-signal-text-tint rounded"
            data-testid="company-code-badge"
          >
            code: {code}
          </span>
        ) : (
          <span className="text-fg-muted">no customer code</span>
        )}
      </div>

      <section aria-labelledby="overview-heading" className="mb-8">
        <h2 id="overview-heading" className="sr-only">
          Company overview
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            label="Cohorts"
            value={detail.cohorts.length}
            detail="Assigned company-wide"
          />
          <SummaryCard label="Members" value={detail.members.length} detail="Current membership" />
          <SummaryCard
            label="Studies"
            value={studyCount}
            detail={
              missingSummaryCount > 0
                ? `${missingSummaryCount} cohort${missingSummaryCount === 1 ? "" : "s"} without study data`
                : "Across assigned cohorts"
            }
          />
          <SummaryCard
            label="Visibility"
            value="Company-wide"
            detail={`${detail.members.length} member${detail.members.length === 1 ? " inherits" : "s inherit"} access`}
          />
        </div>
      </section>

      {/* Cohorts are the living link: every member inherits these. */}
      <section className="mb-8" aria-labelledby="cohorts-heading">
        <div className="mb-3">
          <h2
            id="cohorts-heading"
            className="text-sm font-semibold uppercase tracking-wide text-fg-muted"
          >
            Cohorts
          </h2>
          <p className="mt-1 text-xs text-fg-muted">
            Study statistics come from the live cohort summary. Every member inherits access;
            individual exceptions are managed on the Users page.
          </p>
        </div>

        <div className="overflow-x-auto rounded-frame border border-border bg-surface-2">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="border-b border-border bg-surface-1 text-[11px] uppercase tracking-wide text-fg-muted">
              <tr>
                <th className="px-3 py-2 font-semibold">Cohort</th>
                <th className="px-3 py-2 font-semibold">Visibility</th>
                <th className="px-3 py-2 text-right font-semibold">Studies</th>
                <th className="px-3 py-2 text-right font-semibold">Completed</th>
                <th className="px-3 py-2 text-right font-semibold">Pending</th>
                <th className="px-3 py-2 text-right font-semibold">Failed</th>
                <th className="px-3 py-2 font-semibold">Latest activity</th>
                <th className="px-3 py-2 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {assignedSummaries.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-xs text-fg-muted">
                    No cohorts assigned.
                  </td>
                </tr>
              ) : (
                assignedSummaries.map((cohort) => (
                  <tr key={cohort.cohort_id} data-testid={`cohort-row-${cohort.cohort_id}`}>
                    <td className="px-3 py-3">
                      <div className="font-mono text-xs text-fg">{cohort.cohort_id}</div>
                      <div className="mt-0.5 text-[11px] text-fg-muted">
                        {cohort.summary?.model || "No live study summary"}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className="inline-flex rounded-xs bg-signal-tint px-2 py-0.5 text-[11px] font-medium text-signal-text-tint">
                        All company members
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <CohortMetric value={cohort.summary?.total} />
                    </td>
                    <td className="px-3 py-3 text-right">
                      <CohortMetric value={cohort.summary?.completed} />
                    </td>
                    <td className="px-3 py-3 text-right">
                      <CohortMetric value={cohort.summary?.pending} />
                    </td>
                    <td className="px-3 py-3 text-right">
                      <CohortMetric value={cohort.summary?.failed} />
                    </td>
                    <td className="px-3 py-3 text-xs text-fg-muted">
                      {formatLatestActivity(cohort.summary?.latest_created_at)}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button
                        onClick={() => unassignMut.mutate(cohort.cohort_id)}
                        disabled={unassignMut.isPending}
                        className="text-xs text-fg-muted hover:text-white disabled:opacity-50"
                        aria-label={`Remove ${cohort.cohort_id}`}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex max-w-2xl items-center gap-2">
          <span className="sr-only" id="assign-cohort-label">
            Assign a cohort
          </span>
          <select
            value={cohortToAdd}
            onChange={(e) => setCohortToAdd(e.target.value)}
            aria-labelledby="assign-cohort-label"
            data-testid="cohort-select"
            className="flex-1 bg-surface-2 border border-border-strong rounded px-2 py-1.5 text-xs text-fg"
          >
            <option value="">Select a cohort to assign…</option>
            <optgroup label="Reader / inference">
              {readerAvailable.map((id) => (
                <option key={`r-${id}`} value={id}>
                  {id}
                </option>
              ))}
            </optgroup>
            <optgroup label="Labeling">
              {labelingAvailable.map((id) => (
                <option key={`l-${id}`} value={id}>
                  {id}
                </option>
              ))}
            </optgroup>
          </select>
          <Button
            size="sm"
            disabled={!cohortToAdd || assignMut.isPending}
            data-testid="assign-cohort"
            onClick={() => {
              if (cohortToAdd) {
                assignMut.mutate(cohortToAdd);
                setCohortToAdd("");
              }
            }}
          >
            <FaPlus className="w-3 h-3 mr-1" /> Assign
          </Button>
        </div>
      </section>

      {/* Members */}
      <section aria-labelledby="members-heading">
        <h2
          id="members-heading"
          className="text-sm font-semibold uppercase tracking-wide text-fg-muted mb-2"
        >
          Members
        </h2>
        <div className="space-y-1 mb-3">
          {detail.members.length === 0 ? (
            <span className="text-xs text-fg-muted">No members.</span>
          ) : (
            detail.members.map((m) => (
              <div
                key={m.user_id}
                className="flex items-center justify-between px-3 py-1.5 bg-surface-2 border border-border-strong/50 rounded text-xs"
                data-testid={`member-${m.user_id}`}
              >
                <span>
                  {m.email || m.user_id}
                  <span className="text-fg-muted ml-2">· {m.user_type}</span>
                </span>
                <button
                  onClick={() => removeMemberMut.mutate(m.user_id)}
                  className="text-fg-muted hover:text-white"
                  aria-label={`Remove ${m.email || m.user_id}`}
                >
                  <FaTimes className="w-2.5 h-2.5" />
                </button>
              </div>
            ))
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={userToAdd}
            onChange={(e) => setUserToAdd(e.target.value)}
            data-testid="member-select"
            className="flex-1 bg-surface-2 border border-border-strong rounded px-2 py-1.5 text-xs text-fg"
          >
            <option value="">Add a member…</option>
            {nonMembers.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.email || u.user_id} ({u.user_type})
              </option>
            ))}
          </select>
          <Button
            size="sm"
            disabled={!userToAdd || addMemberMut.isPending}
            data-testid="add-member"
            onClick={() => {
              if (userToAdd) {
                addMemberMut.mutate(userToAdd);
                setUserToAdd("");
              }
            }}
          >
            <FaPlus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
      </section>
    </div>
  );
}
